import React, { useState } from 'react';
import {
  Send, CheckCircle, AlertCircle, Loader2, Download, AlertTriangle, ShieldCheck,
} from 'lucide-react';
import Modal from '../../ui/Modal';
import {
  submitInvoiceToKsef, downloadKsefUpo, parseKsefError,
  KsefInvoiceState, KsefEnvironment, KSEF_ENV_META, isKsefEnvironment,
} from '../../../api/ksef';
import { KsefEnvBadge } from './KsefChip';

interface Props {
  invoiceId: number;
  invoiceNumber: string;
  buyerName?: string;
  total?: string;
  /**
   * Which KSeF environment this submission will actually hit. Required, and
   * explicitly nullable — there is no safe default. Resolve it with
   * `resolveKsefEnv(invoice)` rather than guessing.
   */
  environment: KsefEnvironment | null;
  onClose: () => void;
  onDone: (ksef: KsefInvoiceState) => void;
}

type Mode = 'review' | 'processing' | 'accepted' | 'rejected';

const KsefSubmitModal: React.FC<Props> = ({
  invoiceId, invoiceNumber, buyerName, total, environment, onClose, onDone,
}) => {
  const [mode, setMode] = useState<Mode>('review');
  const [result, setResult] = useState<KsefInvoiceState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);

  // Once submitted, the response tells us exactly which environment was used —
  // prefer it over the pre-flight prop so the accepted view can never drift.
  const submittedEnv = isKsefEnvironment(result?.environment) ? result!.environment as KsefEnvironment : null;
  const env = submittedEnv ?? environment;
  const meta = env ? KSEF_ENV_META[env] : null;
  const isProd = env === 'prod';
  const canSend = env !== null && (!isProd || acknowledged);

  const run = async () => {
    if (!canSend) return;  // belt-and-braces: the button is already disabled
    setMode('processing');
    setError(null);
    try {
      const res = await submitInvoiceToKsef(invoiceId);
      setResult(res.ksef);
      setMode(res.ksef.status === 'accepted' ? 'accepted' : 'rejected');
      onDone(res.ksef);
    } catch (e: any) {
      setError(await parseKsefError(e));
      setMode('rejected');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      // A submission in flight must not be interrupted by a stray Escape or an
      // off-panel click — KSeF has already been called at that point.
      dismissible={mode !== 'processing'}
      size="lg"
      title={
        <>
          {mode === 'accepted' ? 'Accepted by KSeF'
            : mode === 'rejected' ? 'Rejected by KSeF'
            : 'Submit to KSeF'}
          <KsefEnvBadge env={env} />
        </>
      }
      description={
        mode === 'accepted' ? 'The invoice has a KSeF number and is legally issued.'
          : mode === 'rejected' ? 'KSeF returned an error. Fix and resubmit.'
          : 'FA(3) XML will be generated, encrypted and submitted to the Ministry of Finance.'
      }
      footerLeft={
        /* Label and host both come from KSEF_ENV_META so they cannot contradict
           each other — the host used to be a hardcoded production literal. */
        <div className="text-xs text-stone-500 flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
          {meta ? `${meta.label.toUpperCase()} · ${meta.host}` : 'Environment unknown'}
        </div>
      }
      footer={
        <>
          {mode === 'review' && (
            <>
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-300 text-sm dark:border-stone-600">
                Cancel
              </button>
              <button
                onClick={run}
                disabled={!canSend}
                title={
                  env === null
                    ? 'The KSeF environment for this seller is unknown.'
                    : isProd && !acknowledged
                      ? 'Confirm you understand this invoice will be legally issued.'
                      : undefined
                }
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-coral-600 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" /> Send to KSeF
              </button>
            </>
          )}
          {mode === 'accepted' && (
            <>
              {result?.has_upo && (
                <button
                  onClick={() => downloadKsefUpo(invoiceId, result?.ksef_number || invoiceNumber)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-stone-300 text-sm dark:border-stone-600"
                >
                  <Download className="w-4 h-4" /> Download UPO
                </button>
              )}
              <button onClick={onClose} className="px-4 py-2 rounded-lg bg-coral-600 text-white text-sm font-medium">
                Done
              </button>
            </>
          )}
          {mode === 'rejected' && (
            <>
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-300 text-sm dark:border-stone-600">
                Close
              </button>
              <button
                onClick={() => { setAcknowledged(false); setMode('review'); }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-coral-600 text-white text-sm font-medium"
              >
                <Send className="w-4 h-4" /> Retry
              </button>
            </>
          )}
        </>
      }
    >
      <>
          {mode === 'review' && (
            <>
              <div className="rounded-xl border border-stone-200 dark:border-stone-700 p-4 text-sm space-y-2">
                <Row label="Invoice" value={invoiceNumber} mono />
                {buyerName && <Row label="Buyer" value={buyerName} />}
                {total && <Row label="Total" value={total} bold />}
                <Row label="Environment" value={meta ? meta.label : 'Unknown'} />
              </div>

              {/* We cannot tell the user what this will do, so we don't let them do it. */}
              {!meta && (
                <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-stone-100 border border-stone-300 text-stone-700 text-sm dark:bg-stone-800 dark:border-stone-600 dark:text-stone-300">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    ViewPoint can’t determine which KSeF environment this seller is connected to,
                    so it won’t submit. Open <b>Invoice Settings → KSeF</b>, check the environment
                    and run <b>Test connection</b>.
                  </span>
                </div>
              )}

              {/* Production carries legal weight — amber for attention, not red for alarm. */}
              {meta && isProd && (
                <div className="mt-4 rounded-lg bg-amber-50 border border-amber-300 text-amber-900 text-sm dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200">
                  <div className="flex items-start gap-2 p-3">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{meta.legal}</span>
                  </div>
                  <label className="flex items-start gap-2 px-3 pb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acknowledged}
                      onChange={(e) => setAcknowledged(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-amber-400 text-coral-600 focus:ring-coral-500"
                    />
                    <span className="font-medium">
                      I understand this invoice will be legally issued in the live KSeF system and
                      cannot be deleted.
                    </span>
                  </label>
                </div>
              )}

              {/* Test/demo: state plainly that nothing legal happens here. */}
              {meta && !isProd && (
                <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-stone-100 border border-stone-200 text-stone-600 text-sm dark:bg-stone-800 dark:border-stone-700 dark:text-stone-300">
                  <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{meta.legal}</span>
                </div>
              )}
            </>
          )}

          {mode === 'processing' && (
            <div className="flex flex-col items-center gap-3 py-10 text-stone-600">
              <Loader2 className="w-8 h-8 animate-spin text-coral-600" />
              <div className="text-sm">Authenticating, encrypting & submitting to KSeF…</div>
              <div className="text-xs text-stone-400">This can take up to a minute.</div>
            </div>
          )}

          {mode === 'accepted' && result && (
            <div className="text-center py-4">
              <div className="mx-auto w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <div className="mt-3 font-semibold text-stone-900 dark:text-white">Accepted by KSeF</div>
              <div className="mt-4 text-left rounded-xl bg-stone-50 dark:bg-stone-800 p-4 text-sm space-y-2">
                <Row label="KSeF number" value={result.ksef_number || '—'} mono />
                <Row label="Reference" value={result.reference_number || '—'} mono />
                {result.accepted_at && (
                  <Row label="Accepted" value={new Date(result.accepted_at).toLocaleString()} />
                )}
              </div>
            </div>
          )}

          {mode === 'rejected' && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <div className="flex items-center gap-2 text-red-700 font-semibold">
                <AlertCircle className="w-5 h-5" /> Rejected by KSeF
              </div>
              <div className="mt-2 text-sm text-red-700">
                {error || result?.error_description || 'KSeF rejected the invoice.'}
                {result?.error_code ? ` (code ${result.error_code})` : ''}
              </div>
            </div>
          )}
      </>
    </Modal>
  );
};

const Row: React.FC<{ label: string; value: string; mono?: boolean; bold?: boolean }> = ({
  label, value, mono, bold,
}) => (
  <div className="flex justify-between gap-4">
    <span className="text-stone-500">{label}</span>
    <span
      className={`${mono ? 'font-mono' : ''} ${bold ? 'font-bold' : 'font-medium'} text-stone-800 dark:text-stone-200 text-right break-all`}
    >
      {value}
    </span>
  </div>
);

export default KsefSubmitModal;
